import { formatoMoneda } from './servicios';
import { numeroComprobante } from '../services/comprobantesService';

// Comprobante de venta (NO fiscal, sin CAI) en PDF con la marca del negocio.
// Dos formatos: 'carta' (A4, para enviar o imprimir) y 'ticket' (80 mm, para
// impresora térmica). jsPDF se carga en diferido.

export function hexARgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [109, 40, 217];
}

// Logo como PNG (sirve para JPG, PNG o WEBP) con su proporción real.
export function cargarLogo(url) {
  return new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const max = 400;
        const k = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        resolve({ data: c.toDataURL('image/png'), proporcion: c.width / c.height });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// Encaja el logo en una caja de lado `caja` (mm) sin deformarlo.
export function medidaLogo(logo, caja) {
  return logo.proporcion >= 1 ? [caja, caja / logo.proporcion] : [caja * logo.proporcion, caja];
}

const fechaTexto = (iso) => new Date(iso).toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });
const horaTexto = (iso) => new Date(iso).toLocaleTimeString('es-HN', { hour: 'numeric', minute: '2-digit' });
const cant = (n) => Number(n).toLocaleString('es-HN', { maximumFractionDigits: 3 });
const AVISO = 'Comprobante interno de venta. No sustituye la factura fiscal.';

function datosNegocio(n) {
  return [
    n.razon_social && n.razon_social.trim().toLowerCase() !== String(n.nombre || '').trim().toLowerCase() ? n.razon_social : null,
    n.rtn ? `RTN: ${n.rtn}` : null,
    n.direccion || null,
    [n.telefono ? `Tel: ${n.telefono}` : null, n.correo || null].filter(Boolean).join('  ·  ') || null,
  ].filter(Boolean);
}

// ── Carta / A4 ────────────────────────────────────────────────────────────
function carta(jsPDF, { negocio: n, venta: v, comprobante: c, logo, cliente }) {
  const fmt = (x) => formatoMoneda(x, n.moneda || 'L');
  const [r, g, b] = hexARgb(n.color_principal);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, M = 15;
  let y = M;

  // Encabezado: logo + datos del negocio (izquierda), número y fecha (derecha).
  let tx = M;
  if (logo) {
    const [lw, lh] = medidaLogo(logo, 24);
    try { doc.addImage(logo.data, 'PNG', M, y, lw, lh, undefined, 'FAST'); tx = M + lw + 5; } catch { tx = M; }
  }
  doc.setTextColor(20, 20, 20); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  // Ancho disponible hasta el cuadro del número (para que nada se monte).
  const bx = W - M - 62;
  const ancho = bx - tx - 5;
  doc.text(doc.splitTextToSize(String(n.nombre || ''), ancho)[0], tx, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90, 90, 90);
  let yy = y + 12;
  datosNegocio(n).forEach((linea) => {
    doc.splitTextToSize(linea, ancho).forEach((l) => { doc.text(l, tx, yy); yy += 4.5; });
  });

  doc.setFillColor(r, g, b); doc.roundedRect(bx, y, 62, 30, 2, 2, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text('COMPROBANTE DE VENTA', bx + 31, y + 7, { align: 'center' });
  doc.setFontSize(14); doc.text(numeroComprobante(c.numero), bx + 31, y + 15, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`Fecha: ${fechaTexto(v.fecha)}  ·  ${horaTexto(v.fecha)}`, bx + 31, y + 23, { align: 'center' });

  y = Math.max(yy, y + 30) + 6;

  // Cliente.
  doc.setDrawColor(230, 230, 230); doc.setFillColor(248, 247, 252);
  doc.roundedRect(M, y, W - 2 * M, 16, 2, 2, 'FD');
  doc.setTextColor(110, 110, 110); doc.setFontSize(8.5); doc.text('CLIENTE', M + 4, y + 5);
  doc.text('RTN', M + 110, y + 5);
  doc.setTextColor(20, 20, 20); doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5);
  doc.text(doc.splitTextToSize(cliente.nombre || 'Consumidor final', 100)[0], M + 4, y + 11.5);
  doc.setFont('helvetica', 'normal');
  doc.text(cliente.rtn || '—', M + 110, y + 11.5);
  y += 24;

  // Detalle.
  const cols = { cant: M + 2, desc: M + 22, precio: W - M - 32, total: W - M - 2 };
  const encabezado = () => {
    doc.setFillColor(r, g, b); doc.rect(M, y - 5, W - 2 * M, 8, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text('CANT.', cols.cant, y); doc.text('DESCRIPCIÓN', cols.desc, y);
    doc.text('PRECIO', cols.precio, y, { align: 'right' }); doc.text('TOTAL', cols.total, y, { align: 'right' });
    y += 8;
  };
  encabezado();
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  v.items.forEach((it) => {
    const lineas = doc.splitTextToSize(it.descripcion, cols.precio - cols.desc - 22);
    if (y + lineas.length * 4.5 > 255) { doc.addPage(); y = M + 5; encabezado(); doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); }
    doc.setTextColor(30, 30, 30);
    doc.text(cant(it.cantidad), cols.cant, y);
    doc.text(lineas, cols.desc, y);
    doc.text(fmt(it.precio_unitario), cols.precio, y, { align: 'right' });
    doc.text(fmt(it.precio_unitario * it.cantidad), cols.total, y, { align: 'right' });
    y += lineas.length * 4.5 + 4;
    doc.setDrawColor(238, 238, 238); doc.line(M, y - 4.2, W - M, y - 4.2);
  });

  // Totales.
  if (y > 230) { doc.addPage(); y = M + 5; }
  y += 3;
  const fila = (etq, val, fuerte) => {
    doc.setFont('helvetica', fuerte ? 'bold' : 'normal'); doc.setFontSize(fuerte ? 12 : 10);
    if (fuerte) doc.setTextColor(r, g, b); else doc.setTextColor(70, 70, 70);
    doc.text(etq, W - M - 70, y); doc.text(val, W - M - 2, y, { align: 'right' });
    y += fuerte ? 8 : 5.5;
  };
  fila('Subtotal', fmt(v.subtotal));
  if (v.descuento > 0) fila('Descuento', `- ${fmt(v.descuento)}`);
  if (v.envio > 0) fila('Envío', fmt(v.envio));
  doc.setDrawColor(200, 200, 200); doc.line(W - M - 70, y - 3, W - M, y - 3); y += 2;
  fila('TOTAL', fmt(v.total), true);
  if (v.credito) { fila('Pagado', fmt(v.pagado)); fila('Saldo pendiente', fmt(v.saldo)); }
  if (v.metodo) { doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(110, 110, 110); doc.text(`Forma de pago: ${v.metodo}`, M, y - 6); }

  // Pie.
  y += 6;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(r, g, b);
  doc.text('¡Gracias por tu compra!', W / 2, y, { align: 'center' }); y += 6;
  if (n.pie_comprobante) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90, 90, 90);
    doc.splitTextToSize(n.pie_comprobante, W - 2 * M).forEach((l) => { doc.text(l, W / 2, y, { align: 'center' }); y += 4.5; });
  }
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(160, 160, 160);
  doc.text(AVISO, W / 2, 286, { align: 'center' });
  doc.text('Hecho con Kontrol · by Automatika.IA', W / 2, 291, { align: 'center' });
  return doc;
}

// ── Ticket 80 mm ──────────────────────────────────────────────────────────
function ticket(jsPDF, datos, alto) {
  const { negocio: n, venta: v, comprobante: c, logo, cliente } = datos;
  const fmt = (x) => formatoMoneda(x, n.moneda || 'L');
  const W = 80, M = 4, C = W / 2;
  const doc = new jsPDF({ unit: 'mm', format: [W, alto] });
  let y = 5;
  const centro = (txt, tam = 8, negrita = false) => {
    doc.setFont('helvetica', negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    doc.splitTextToSize(String(txt), W - 2 * M).forEach((l) => { doc.text(l, C, y, { align: 'center' }); y += tam * 0.45; });
  };
  const raya = () => { doc.setLineDashPattern([0.8, 0.8], 0); doc.setDrawColor(120, 120, 120); doc.line(M, y, W - M, y); doc.setLineDashPattern([], 0); y += 3.5; };
  const par = (izq, der, tam = 8, negrita = false) => {
    doc.setFont('helvetica', negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    doc.text(izq, M, y); doc.text(der, W - M, y, { align: 'right' }); y += tam * 0.45 + 0.6;
  };
  doc.setTextColor(0, 0, 0);

  if (logo) {
    const [lw, lh] = medidaLogo(logo, 18);
    try { doc.addImage(logo.data, 'PNG', C - lw / 2, y, lw, lh, undefined, 'FAST'); y += lh + 3; } catch { /* sin logo */ }
  }
  centro(n.nombre || '', 11, true); y += 0.5;
  datosNegocio(n).forEach((l) => centro(l, 7.5));
  y += 1; raya();
  centro('COMPROBANTE DE VENTA', 9, true);
  centro(numeroComprobante(c.numero), 9, true);
  centro(`${fechaTexto(v.fecha)}  ${horaTexto(v.fecha)}`, 8);
  y += 1;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.splitTextToSize(`Cliente: ${cliente.nombre || 'Consumidor final'}`, W - 2 * M).forEach((l) => { doc.text(l, M, y); y += 3.6; });
  if (cliente.rtn) { doc.text(`RTN: ${cliente.rtn}`, M, y); y += 3.6; }
  y += 0.5; raya();

  v.items.forEach((it) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.splitTextToSize(it.descripcion, W - 2 * M).forEach((l) => { doc.text(l, M, y); y += 3.6; });
    par(`  ${cant(it.cantidad)} x ${fmt(it.precio_unitario)}`, fmt(it.precio_unitario * it.cantidad));
  });
  raya();
  par('Subtotal', fmt(v.subtotal));
  if (v.descuento > 0) par('Descuento', `- ${fmt(v.descuento)}`);
  if (v.envio > 0) par('Envío', fmt(v.envio));
  y += 0.5; par('TOTAL', fmt(v.total), 11, true); y += 0.5;
  if (v.credito) { par('Pagado', fmt(v.pagado)); par('Saldo pendiente', fmt(v.saldo), 8, true); }
  if (v.metodo) par('Forma de pago', v.metodo);
  y += 1; raya();
  centro('¡Gracias por tu compra!', 9, true);
  if (n.pie_comprobante) centro(n.pie_comprobante, 7.5);
  y += 1;
  doc.setTextColor(90, 90, 90);
  centro(AVISO, 6.5);
  centro('Hecho con Kontrol · by Automatika.IA', 6.5);
  return { doc, y: y + 4 };
}

// Arma el PDF. formato: 'carta' | 'ticket'. Devuelve { doc, nombreArchivo }.
export async function armarComprobantePdf({ negocio, venta, comprobante, cliente, formato = 'carta' }) {
  const { jsPDF } = await import('jspdf');
  const datos = { negocio: negocio || {}, venta, comprobante, cliente: cliente || {}, logo: await cargarLogo(negocio?.logo_url) };
  let doc;
  if (formato === 'ticket') {
    const medida = ticket(jsPDF, datos, 2000).y; // primero se mide el largo
    doc = ticket(jsPDF, datos, Math.max(medida, 60)).doc;
  } else {
    doc = carta(jsPDF, datos);
  }
  const nombreArchivo = `${numeroComprobante(comprobante.numero)}${formato === 'ticket' ? '-ticket' : ''}.pdf`;
  return { doc, nombreArchivo };
}

export function descargarPdf({ doc, nombreArchivo }) {
  try { doc.save(nombreArchivo); }
  catch { try { window.open(doc.output('bloburl'), '_blank'); } catch { /* nada */ } }
}

// Comparte el PDF (WhatsApp, correo...) desde el celular. Si el dispositivo no
// permite compartir archivos, lo descarga.
export async function compartirPdf({ doc, nombreArchivo }, texto) {
  try {
    const archivo = new File([doc.output('blob')], nombreArchivo, { type: 'application/pdf' });
    if (navigator.canShare && navigator.canShare({ files: [archivo] })) {
      await navigator.share({ files: [archivo], title: nombreArchivo, text: texto });
      return true;
    }
  } catch (e) {
    if (e?.name === 'AbortError') return true; // la persona cerró el menú
  }
  descargarPdf({ doc, nombreArchivo });
  return false;
}

// Abre el PDF para imprimir (impresora normal o térmica).
export function imprimirPdf({ doc }) {
  try {
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  } catch { /* nada */ }
}
